import { Truncate } from "@dropbox/dig-components/truncate";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import * as React from "react";
import { CalendarIcon } from "../../calendar-icon/CalendarIcon";
import {
  _CalendarCardContent as CardContent,
  _CalendarContainer as Container,
} from "../container";
import type { CalendarEvent, CalendarEventLabels } from "../types";
import styles from "./CalendarEventCard.module.css";
import { ExpandableDescription } from "./ExpandableDescription";
import { useLabeledEventDataGrid } from "./useLabeledEventDataGrid";

export interface CalendarEventCardProps {
  event: CalendarEvent;
  labels: Pick<
    CalendarEventLabels,
    | "getNumAttendeesLabel"
    | "getFormattedEventTime"
    | "getRelativeTime"
    | "timeLabel"
    | "attendingLabel"
    | "descriptionLabel"
    | "dateFormatterFn"
    | "getFullDate"
    | "showMore"
    | "showLess"
    | "guestLabel"
    | "hostLabel"
    | "relativeTimeLabel"
  >;
}

/**
 * A card component for displaying calendar event information in chat responses
 */
export const CalendarEventCard = ({
  event,
  labels,
}: CalendarEventCardProps) => {
  const eventDate = React.useMemo(
    () => new Date(event.startTime),
    [event.startTime],
  );

  const fullDate = React.useMemo(() => {
    return labels.getFullDate(eventDate);
  }, [eventDate, labels]);

  const labeledEventData = useLabeledEventDataGrid(event, labels);

  // Group items into pairs for 2-column layout
  const groupedItems = React.useMemo(() => {
    const groups: Array<Array<(typeof labeledEventData)[0]>> = [];
    for (let i = 0; i < labeledEventData.length; i += 2) {
      groups.push(labeledEventData.slice(i, i + 2));
    }
    return groups;
  }, [labeledEventData]);

  const leftColumn = (
    <>
      <CalendarIcon
        date={eventDate}
        dateFormatterFn={labels.dateFormatterFn}
        size="2xlarge"
      />

      <div className={styles.leftColumnTitle}>
        <Title
          weightVariant="emphasized"
          size="medium"
          tagName={"h3"}
          className={styles.eventTitle}
        >
          <Truncate
            style={{ justifyContent: "center" }}
            lines={2}
            tooltipControlProps={{ auto: true, placement: "top" }}
          >
            {event.title}
          </Truncate>
        </Title>
        <Box display="flex" justifyContent="center" marginTop="Micro XSmall">
          <Text size="large" color="subtle" className={styles.dateText}>
            {fullDate}
          </Text>
        </Box>
      </div>
    </>
  );

  const rightColumn = (
    <div className={styles.infoGrid}>
      {groupedItems.map((group, groupIndex) => (
        <React.Fragment key={`group-${groupIndex}`}>
          {group.map((item) => (
            <div key={item.id} className={styles.cell}>
              {item.label && (
                <Text color="subtle" size="medium">
                  {item.label}
                </Text>
              )}
              {item.content}
            </div>
          ))}

          {/* If odd number of items in group, add empty cell */}
          {group.length === 1 && <div className={styles.cell} />}

          {/* Add divider after first row and after each row except the last one */}
          {(groupIndex === 0 || groupIndex < groupedItems.length - 1) && (
            <div className={styles.divider} />
          )}
        </React.Fragment>
      ))}

      {event.sanitizedDescription && (
        <>
          <div className={styles.fullWidthCell}>
            <ExpandableDescription
              sanitizedDescription={event.sanitizedDescription}
              labels={{
                descriptionLabel: labels.descriptionLabel,
                showMore: labels.showMore,
                showLess: labels.showLess,
              }}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <Container>
      <CardContent
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        leftColumnAlignment="flex-start"
      />
    </Container>
  );
};
